import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import Axios from 'axios';
import { queryClient } from 'QueryClientProvider';

import { useUserGuiConfig } from '@contexts/UserGuiConfigContext';

const fetchGlobalFeatures = async () => {
    return await Axios.get('/ManageFeatures/Features/FullList');
};

const staleTime = Infinity;
const cacheTime = Infinity;

const prefetchFeatures = () => {
    queryClient.prefetchQuery({
        queryKey: ['global-features'],
        queryFn: () => fetchGlobalFeatures(),
        staleTime: staleTime,
        cacheTime: cacheTime,
        retry: false,
    });
};

const useGlobalFeatures = () => {
    return useQuery({
        queryKey: ['global-features'],
        queryFn: fetchGlobalFeatures,
        staleTime: staleTime,
        cacheTime: cacheTime,
        retry: false,
    });
};

const forceReloadGlobalFeatures = async () => {
    await queryClient.invalidateQueries({ queryKey: ['global-features'] });
    return queryClient.fetchQuery({
        queryKey: ['global-features'],
        queryFn: fetchGlobalFeatures,
        staleTime: staleTime,
        cacheTime: cacheTime,
        retry: false,
    });
};

const fetchGlobalFeatureSets = async () => {
    return await Axios.get('/ManageFeatures/Featuresets/FullList');
};

const prefetchFeatureSets = () => {
    queryClient.prefetchQuery({
        queryKey: ['global-feature-sets'],
        queryFn: () => fetchGlobalFeatureSets(),
        staleTime: staleTime,
        cacheTime: cacheTime,
        retry: false,
    });
};

const useGlobalFeatureSets = () => {
    return useQuery({
        queryKey: ['global-feature-sets'],
        queryFn: fetchGlobalFeatureSets,
        staleTime: staleTime,
        cacheTime: cacheTime,
        retry: false,
    });
};

const forceReloadGlobalFeatureSets = () => {
    return queryClient.invalidateQueries({ queryKey: ['global-feature-sets'] });
};

const fetchGlobalConversions = async () => {
    return await Axios.get('/ManageFeatures/GetGlobalConversions');
};

const prefetchConversions = async () => {
    try {
        return await queryClient.fetchQuery({
            queryKey: ['global-conversions'],
            queryFn: () => fetchGlobalConversions(),
            staleTime: staleTime,
            cacheTime: cacheTime,
        });
    } catch (error) {
        console.error(error);
    }
};

const useGlobalConversions = () => {
    return useQuery({
        queryKey: ['global-conversions'],
        queryFn: fetchGlobalConversions,
        staleTime: staleTime,
        cacheTime: cacheTime,
        retry: false,
    });
};

const forceReloadGlobalConversions = () => {
    return queryClient.invalidateQueries({ queryKey: ['global-conversions'] });
};

/**
 * Indici per la risoluzione dei dati globali delle features, al posto delle `.find()`
 * ripetute dentro il rendering. Le query hanno staleTime/cacheTime = Infinity, quindi
 * gli indici si costruiscono una volta per sessione e cambiano solo con le preferenze utente.
 */
const useFeaturesIndex = () => {
    const { data: features } = useGlobalFeatures();
    const { data: conversions } = useGlobalConversions();
    const userGuiConfig = useUserGuiConfig();

    return useMemo(
        () => ({
            featureById: new Map((features || []).map((feature) => [feature.ID, feature])),
            conversionById: new Map((conversions || []).map((conversion) => [conversion.ID, conversion])),
            metricPrefByConversionId: new Map((userGuiConfig.metrics || []).map((pref) => [pref.ID, pref])),
        }),
        [features, conversions, userGuiConfig.metrics]
    );
};

export {
    prefetchFeatures,
    prefetchFeatureSets,
    prefetchConversions,
    useGlobalFeatures,
    useGlobalFeatureSets,
    useGlobalConversions,
    useFeaturesIndex,
    forceReloadGlobalFeatures,
    forceReloadGlobalFeatureSets,
    forceReloadGlobalConversions,
};
